import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { BellIcon, ChevronDown, StarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { DocumentToolbarGroup } from "./DocumentToolbarGroup";
import { toolbarTier } from "./documentToolbarTiers";

export type DocumentFollowGroupProps = {
  isReady: boolean;
  isFavorite: boolean;
  isSubscribed: boolean;
  favoriteLabel: string;
  subscribeLabel: string;
  favoriteLoading: boolean;
  subscribeLoading: boolean;
  onFavorite: () => void;
  onSubscribe: () => void;
  /**
   * Names the toolbar this group belongs to, e.g. `legislation`. The buttons
   * keep the ids the rest of the app knows them by (`…-favorite-btn`), and
   * the folded menu carries `…-menu-item` copies so both forms stay
   * addressable and every id stays unique.
   */
  idPrefix: string;
};

/**
 * `aria-disabled` rather than `disabled`: the shared button turns off pointer
 * events when it is disabled, which takes the tooltip — and so the label — with
 * it while favourites and subscriptions are still loading.
 */
const INERT = "aria-disabled:cursor-not-allowed aria-disabled:opacity-50";

const starClasses = (isFavorite: boolean) =>
  cn("size-4", isFavorite && "fill-warning stroke-warning");

const bellClasses = (isSubscribed: boolean) =>
  cn("size-4", isSubscribed && "fill-warning stroke-warning");

/**
 * "Følg": favourite and subscribe, kept together because both are states the
 * reader has to be able to read off the toolbar, not actions they fire and
 * forget. Below the medium tier the pair folds into a single star menu, which
 * still shows whether the document is a favourite.
 */
export const DocumentFollowGroup = ({
  isReady,
  isFavorite,
  isSubscribed,
  favoriteLabel,
  subscribeLabel,
  favoriteLoading,
  subscribeLoading,
  onFavorite,
  onSubscribe,
  idPrefix,
}: DocumentFollowGroupProps) => {
  const favoriteButtonId = `${idPrefix}-favorite-btn`;
  const subscribeButtonId = `${idPrefix}-subscribe-btn`;
  const favoriteBusy = !isReady || favoriteLoading;
  const subscribeBusy = !isReady || subscribeLoading;

  return (
    <>
      <DocumentToolbarGroup
        label="Følg"
        className={cn("shrink-0", toolbarTier.fromMediumFlex)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={cn("size-7", INERT)}
              aria-disabled={favoriteBusy}
              onClick={() => {
                if (!favoriteBusy) onFavorite();
              }}
              id={favoriteButtonId}
              aria-label={favoriteLabel}
              aria-pressed={isFavorite}
            >
              {favoriteBusy ? (
                <Spinner className="size-4" />
              ) : (
                <StarIcon className={starClasses(isFavorite)} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{favoriteLabel}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={cn("size-7", INERT)}
              aria-disabled={subscribeBusy}
              onClick={() => {
                if (!subscribeBusy) onSubscribe();
              }}
              id={subscribeButtonId}
              aria-label={subscribeLabel}
              aria-pressed={isSubscribed}
            >
              {subscribeBusy ? (
                <Spinner className="size-4" />
              ) : (
                <BellIcon className={bellClasses(isSubscribed)} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{subscribeLabel}</p>
          </TooltipContent>
        </Tooltip>
      </DocumentToolbarGroup>

      <DocumentToolbarGroup
        label="Følg"
        className={cn("shrink-0", toolbarTier.belowMedium)}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2"
              id={`${favoriteButtonId}-menu`}
              aria-label="Følg dokumentet"
            >
              <StarIcon className={starClasses(isFavorite)} />
              <ChevronDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              disabled={favoriteBusy}
              onClick={onFavorite}
              id={`${favoriteButtonId}-menu-item`}
            >
              <StarIcon className={starClasses(isFavorite)} />
              {favoriteLabel}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              disabled={subscribeBusy}
              onClick={onSubscribe}
              id={`${subscribeButtonId}-menu-item`}
            >
              <BellIcon className={bellClasses(isSubscribed)} />
              {subscribeLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </DocumentToolbarGroup>
    </>
  );
};

export default DocumentFollowGroup;
